import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { addDoc, collection, getDocs, query, where, doc, updateDoc } from 'firebase/firestore';
import { firestore, storage } from '../../database/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  X,
  Briefcase,
  Calendar,
  DollarSign,
  Users,
  FileText,
  CheckCircle,
  Clock,
  Target,
  AlertCircle,
  Image as ImageIcon,
} from 'lucide-react';
import { toast } from 'react-toastify';

export default function ProjectForm({
  organizationId,
  onProjectAdded,
  onClose,
  editingProject = null,
  currentUserId = null,
}) {
  const [formData, setFormData] = useState({
    name: editingProject?.name || '',
    description: editingProject?.description || '',
    clientId: editingProject?.clientId || '',
    clientName: editingProject?.clientName || '',

    status: editingProject?.status || 'planning',
    priority: editingProject?.priority || 'medium',
    category: editingProject?.category || '',

    startDate: editingProject?.startDate || '',
    endDate: editingProject?.endDate || '',
    estimatedHours: editingProject?.estimatedHours || '',

    budget: editingProject?.budget || '',
    actualCost: editingProject?.actualCost || '',
    currency: editingProject?.currency || 'USD',

    projectManager: editingProject?.projectManager || '',
    teamMembers: editingProject?.teamMembers || [],

    deliverables: editingProject?.deliverables || '',
    milestones: editingProject?.milestones || [],
    risks: editingProject?.risks || '',
    notes: editingProject?.notes || '',
    images: editingProject?.images || [],
  });

  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [clients, setClients] = useState([]);
  const [errors, setErrors] = useState({});

  const projectStatuses = [
    { value: 'planning', label: 'Planning', color: 'bg-blue-100 text-blue-700' },
    { value: 'in_progress', label: 'In Progress', color: 'bg-orange-100 text-orange-700' },
    { value: 'on_hold', label: 'On Hold', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
  ];

  const priorities = [
    { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
    { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
    { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
    { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' },
  ];

  const categories = [
    'Web Development',
    'Mobile App',
    'Design',
    'Consulting',
    'Training',
    'Maintenance',
    'Integration',
    'Custom Software',
    'E-commerce',
    'Other',
  ];

  const currencies = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY'];

  useEffect(() => {
    loadClients();
    // Initialize image previews from existing project images
    if (editingProject?.images && editingProject.images.length > 0) {
      setImagePreviews(editingProject.images);
    }
  }, [organizationId, editingProject]);

  const loadClients = async () => {
    try {
      const orgSnapshot = await getDocs(
        collection(firestore, 'companyProfiles', organizationId, 'clients')
      );
      const userSnapshot = currentUserId
        ? await getDocs(collection(firestore, 'companyClient', currentUserId, 'clients'))
        : { docs: [] };
      const combinedDocs = [...orgSnapshot.docs, ...userSnapshot.docs];
      const clientsData = combinedDocs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClients(clientsData);
    } catch (error) {
      toast.error('Error loading clients:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Project name is required';
    if (!formData.description.trim()) newErrors.description = 'Project description is required';
    if (!formData.clientId) newErrors.clientId = 'Client is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.budget) newErrors.budget = 'Budget is required';

    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        newErrors.endDate = 'End date must be after start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const selectedClient = clients.find(c => c.id === formData.clientId);

      if (editingProject?.id) {
        const ref = doc(
          firestore,
          'companyProfiles',
          organizationId,
          'projects',
          editingProject.id
        );
        const updateData = {
          ...formData,
          clientName: selectedClient?.companyName || formData.clientName,
          updatedAt: new Date(),
        };
        await updateDoc(ref, updateData);
        onProjectAdded({ id: editingProject.id, ...editingProject, ...updateData });
        onClose();
        return;
      }

      const projectData = {
        ...formData,
        organizationId,
        clientName:
          selectedClient?.companyName || selectedClient?.organization || formData.clientName,
        clientEmail: selectedClient?.email || '',
        createdAt: new Date(),
        updatedAt: new Date(),
        progress: 0,
        actualHours: 0,
      };

      const docRef = await addDoc(
        collection(firestore, 'companyProfiles', organizationId, 'projects'),
        projectData
      );

      const newProject = {
        id: docRef.id,
        ...projectData,
      };

      onProjectAdded(newProject);
      onClose();
    } catch (error) {
      toast.error('Error adding project:', error);
      alert('Error adding project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const addMilestone = () => {
    const newMilestone = {
      id: Date.now(),
      title: '',
      date: '',
      description: '',
      completed: false,
    };
    setFormData(prev => ({
      ...prev,
      milestones: [...prev.milestones, newMilestone],
    }));
  };

  const updateMilestone = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.map((milestone, i) =>
        i === index ? { ...milestone, [field]: value } : milestone
      ),
    }));
  };

  const removeMilestone = index => {
    setFormData(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = async e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingImages(true);
    try {
      const uploadPromises = files.map(async file => {
        const timestamp = Date.now();
        const fileName = `${organizationId}/projects/${timestamp}_${file.name}`;
        const storageRef = ref(storage, fileName);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        return {
          url: downloadURL,
          name: file.name,
          type: file.type,
          size: file.size,
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      const newImages = [...formData.images, ...uploadedImages];
      setFormData(prev => ({ ...prev, images: newImages }));
      setImagePreviews(newImages);
      toast.success(`${uploadedImages.length} image(s) uploaded successfully`);
    } catch (error) {
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
      e.target.value = '';
    }
  };

  const removeImage = index => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, images: newImages }));
    setImagePreviews(newImages);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-h-[90vh] w-[92%] max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl sm:w-full md:max-w-4xl"
      >
        <div className="sticky top-0 border-b border-slate-200 bg-white p-4 md:p-6">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-xl font-semibold text-slate-900 md:text-2xl">
              {editingProject ? 'Edit Project' : 'Add New Project'}
            </h2>
            <button onClick={onClose} className="rounded-full p-2 hover:bg-slate-100">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-8 p-4 md:p-6">
          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Basic Information</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Project Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => handleInputChange('name', e.target.value)}
                  className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                    errors.name ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="Enter project name"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Project Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={e => handleInputChange('description', e.target.value)}
                  rows={3}
                  className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                    errors.description ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="Describe the project objectives and scope..."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Client *</label>
                  <select
                    value={formData.clientId}
                    onChange={e => handleInputChange('clientId', e.target.value)}
                    className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                      errors.clientId ? 'border-red-300' : 'border-slate-300'
                    }`}
                  >
                    <option value="">Select client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.companyName}
                      </option>
                    ))}
                  </select>
                  {errors.clientId && (
                    <p className="mt-1 text-sm text-red-600">{errors.clientId}</p>
                  )}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">Category</label>
                  <select
                    value={formData.category}
                    onChange={e => handleInputChange('category', e.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  >
                    <option value="">Select category</option>
                    {categories.map(category => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">
                    Project Manager
                  </label>
                  <input
                    type="text"
                    value={formData.projectManager}
                    onChange={e => handleInputChange('projectManager', e.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                    placeholder="Project manager name"
                  />
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Status & Priority</h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Status</label>
                <select
                  value={formData.status}
                  onChange={e => handleInputChange('status', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                >
                  {projectStatuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Priority</label>
                <select
                  value={formData.priority}
                  onChange={e => handleInputChange('priority', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                >
                  {priorities.map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Timeline</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Start Date *
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={e => handleInputChange('startDate', e.target.value)}
                  className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                    errors.startDate ? 'border-red-300' : 'border-slate-300'
                  }`}
                />
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.startDate}</p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={e => handleInputChange('endDate', e.target.value)}
                  className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                    errors.endDate ? 'border-red-300' : 'border-slate-300'
                  }`}
                />
                {errors.endDate && <p className="mt-1 text-sm text-red-600">{errors.endDate}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Estimated Hours
                </label>
                <input
                  type="number"
                  value={formData.estimatedHours}
                  onChange={e => handleInputChange('estimatedHours', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Financial Information</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Budget *</label>
                <input
                  type="number"
                  value={formData.budget}
                  onChange={e => handleInputChange('budget', e.target.value)}
                  className={`w-full rounded-xl border px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600 ${
                    errors.budget ? 'border-red-300' : 'border-slate-300'
                  }`}
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
                {errors.budget && <p className="mt-1 text-sm text-red-600">{errors.budget}</p>}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Actual Cost</label>
                <input
                  type="number"
                  value={formData.actualCost}
                  onChange={e => handleInputChange('actualCost', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="0.00"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Currency</label>
                <select
                  value={formData.currency}
                  onChange={e => handleInputChange('currency', e.target.value)}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                >
                  {currencies.map(currency => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div>
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">Milestones</h3>
              <button
                onClick={addMilestone}
                className="flex items-center space-x-2 rounded-lg bg-slate-900 px-3 py-1 text-sm text-white hover:opacity-90"
              >
                <Target className="h-4 w-4" />
                <span>Add Milestone</span>
              </button>
            </div>

            <div className="space-y-3">
              {formData.milestones.map((milestone, index) => (
                <div key={milestone.id} className="rounded-lg border border-slate-200 p-4">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                    <input
                      type="text"
                      value={milestone.title}
                      onChange={e => updateMilestone(index, 'title', e.target.value)}
                      className="rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                      placeholder="Milestone title"
                    />
                    <input
                      type="date"
                      value={milestone.date}
                      onChange={e => updateMilestone(index, 'date', e.target.value)}
                      className="rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                    />
                    <input
                      type="text"
                      value={milestone.description}
                      onChange={e => updateMilestone(index, 'description', e.target.value)}
                      className="rounded-lg border border-slate-300 px-3 py-2 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                      placeholder="Description"
                    />
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => updateMilestone(index, 'completed', !milestone.completed)}
                        className={`rounded-lg px-3 py-2 text-sm ${
                          milestone.completed
                            ? 'bg-green-100 text-green-700'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {milestone.completed ? 'Completed' : 'Pending'}
                      </button>
                      <button
                        onClick={() => removeMilestone(index)}
                        className="rounded-lg bg-red-100 p-2 text-red-600 hover:bg-red-200"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Project Images</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Upload Project Images
                </label>
                <div className="rounded-xl border-2 border-dashed border-slate-300 p-4">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploadingImages}
                    className="hidden"
                    id="project-image-upload"
                  />
                  <label
                    htmlFor="project-image-upload"
                    className="flex cursor-pointer flex-col items-center justify-center gap-2"
                  >
                    <ImageIcon className="h-8 w-8 text-slate-400" />
                    <span className="text-sm text-slate-600">
                      {uploadingImages ? 'Uploading...' : 'Click to upload images or drag and drop'}
                    </span>
                    <span className="text-xs text-slate-500">PNG, JPG, GIF up to 10MB</span>
                  </label>
                </div>
              </div>

              {imagePreviews.length > 0 && (
                <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                  {imagePreviews.map((image, index) => (
                    <div key={index} className="group relative">
                      <img
                        src={image.url}
                        alt={image.name || `Project image ${index + 1}`}
                        className="h-32 w-full rounded-lg object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute right-2 top-2 rounded-full bg-red-500 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-lg font-semibold text-slate-900">Additional Information</h3>
            <div className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Deliverables
                </label>
                <textarea
                  value={formData.deliverables}
                  onChange={e => handleInputChange('deliverables', e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="List project deliverables..."
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">
                  Risks & Challenges
                </label>
                <textarea
                  value={formData.risks}
                  onChange={e => handleInputChange('risks', e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="Identify potential risks and challenges..."
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-slate-700">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => handleInputChange('notes', e.target.value)}
                  rows={3}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-slate-600"
                  placeholder="Additional notes..."
                />
              </div>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-slate-200 bg-white p-6">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="rounded-xl border border-slate-300 px-6 py-2 text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center space-x-2 rounded-xl bg-slate-900 px-6 py-2 text-white hover:opacity-90 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  <span>Save Project</span>
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
